import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { colorFor } from '../../../utils/itemPalette';
import { fpColors } from '../../../styles/theme';
import type { InventoryItemResponse } from '../../../api/types';
import type { PrimaryPhotoRecord } from '../segmentSlice';

/**
 * Hero card for the Done view — one per accepted item, displaying
 * the post-confirmation primary photo as it streams in.
 *
 * Photo lifecycle (source: PrimaryPhotoRecord.state):
 *   idle / waiting → animated skeleton tinted with the item's palette
 *   ready          → AuraSR-upscaled crop on a checker-ish backdrop
 *   fallback       → original room photo (`storage_url`) + a small
 *                    "original" badge
 *   timeout/error  → same room-photo fallback + a "couldn't refine"
 *                    chip; user can retry from the polish pass
 *
 * Card shows: photo (square 1:1), name + value below, plus a tiny
 * source-badge chip (UPSCALED / CROP / ORIGINAL).
 */

interface PrimaryPhotoCardProps {
  item: InventoryItemResponse;
  photo?: PrimaryPhotoRecord;
  /** Fallback room-photo URL when the per-item stream times out or
   *  we don't yet have a URL — pulled from `sceneCtx.storage_url`. */
  fallbackRoomPhotoUrl?: string | null;
}

export const PrimaryPhotoCard: React.FC<PrimaryPhotoCardProps> = ({
  item,
  photo,
  fallbackRoomPhotoUrl,
}) => {
  const id = item.id ?? -1;
  const palette = colorFor(id);

  const status = photo?.state ?? 'idle';
  const url =
    status === 'ready' || status === 'fallback'
      ? photo?.url
      : status === 'timeout' || status === 'error'
        ? fallbackRoomPhotoUrl
        : undefined;
  // The image URL above is the primary thing to show. Fall back to
  // the room photo on terminal failures so the card never goes blank.
  const showImage = !!url;

  return (
    <View style={[styles.card, { borderColor: `${palette.solid}40` }]}>
      <View style={styles.photoFrame}>
        {showImage ? (
          // Native <img> on web for simple preloading + transparent
          // PNG support (primary photos are RGBA on transparent bg).
          <img
            src={url || ''}
            style={styles.photo as any}
            alt={item.item_name || 'item'}
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
          />
        ) : (
          <View
            style={[
              StyleSheet.absoluteFill,
              styles.skeleton,
              { backgroundColor: palette.faint },
            ]}
          >
            <MaterialIcons
              name="image"
              size={28}
              color={`${palette.solid}90`}
            />
            <Text style={[styles.skeletonText, { color: palette.solid }]}>
              {status === 'waiting' && photo?.elapsed_seconds
                ? `Polishing\u2026 ${Math.round(photo.elapsed_seconds)}s`
                : 'Polishing\u2026'}
            </Text>
          </View>
        )}
        {photo?.source ? (
          <View
            style={[
              styles.sourcePill,
              status === 'ready'
                ? { backgroundColor: palette.solid }
                : { backgroundColor: 'rgba(0,0,0,0.55)' },
            ]}
          >
            <Text
              style={[
                styles.sourceText,
                status === 'ready'
                  ? { color: palette.onSolid }
                  : { color: '#fff' },
              ]}
            >
              {photo.source === 'crop_upscale'
                ? 'UPSCALED'
                : photo.source === 'crop_only'
                  ? 'CROPPED'
                  : 'ORIGINAL'}
            </Text>
          </View>
        ) : null}
        {status === 'timeout' || status === 'error' ? (
          <View style={styles.warnPill}>
            <MaterialIcons name="warning" size={10} color="#92400E" />
            <Text style={styles.warnText}>
              {status === 'timeout' ? 'TIMED OUT' : 'FAILED'}
            </Text>
          </View>
        ) : null}
      </View>
      <View style={styles.body}>
        <Text style={styles.name} numberOfLines={1}>
          {item.item_name || 'Unnamed'}
        </Text>
        <Text style={styles.meta} numberOfLines={1}>
          {item.item_category || 'uncategorized'}
          {typeof item.estimated_value === 'number' &&
          item.estimated_value > 0
            ? ` · $${Math.round(item.estimated_value)}`
            : ''}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 14,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 4px 14px rgba(31,26,34,0.10)' } as any)
      : null),
  },
  photoFrame: {
    width: '100%',
    aspectRatio: 1,
    backgroundColor: '#0e0e10',
    position: 'relative',
  },
  photo: {
    width: '100%',
    height: '100%',
    // Image-fit sweep (2026-06-23): show the whole item photo
    // instead of cropping to fill. The card bg (#0e0e10 above)
    // serves as the letterbox tone.
    objectFit: 'contain',
    display: 'block',
  } as any,
  skeleton: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    ...(Platform.OS === 'web'
      ? ({
          // Subtle shimmer.
          backgroundImage:
            'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0) 100%)',
          backgroundSize: '200% 100%',
          animation: 'segShimmer 1400ms linear infinite',
        } as any)
      : null),
  },
  skeletonText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  sourcePill: {
    position: 'absolute',
    top: 8,
    left: 8,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
  },
  sourceText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 9,
    letterSpacing: 0.6,
  },
  warnPill: {
    position: 'absolute',
    top: 8,
    right: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: '#FEF3C7',
  },
  warnText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 9,
    letterSpacing: 0.5,
    color: '#92400E',
  },
  body: {
    padding: 10,
    gap: 2,
  },
  name: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  meta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
});
